import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { CountryReturnRate } from '@/domain/returns'
import { useCountries } from '@/application/queries'
import { useFilter } from '@/application/use-filter'
import { Country, toCountryCodes, toCountryName } from '@/domain/countries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[],
  chartMetric?:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
) {
  return [
    'countriesReturnRate',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections,
    chartMetric
  ]
}

export function useCountriesReturnRate(
  startDate?: string,
  endDate?: string,
  chartMetric?:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  async function callCountriesReturnRate() {
    if (!ecommerceIds.length) return
    return AnalyticsApi.getCountriesReturnRate({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      orderingValue: 'SOLD',
      orderingDirection: 'DESC',
      pageNumber: 1,
      pageLength: 200,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections,
      chartMetric: chartMetric ?? 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    })
  }

  const countriesReturnRateQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections,
      chartMetric
    ),
    queryFn: callCountriesReturnRate,
    staleTime: 1000 * 60 * 15,
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  return {
    countriesReturnRate: toCountriesReturnRateWithCountryName(
      countries,
      countriesReturnRateQuery.data ?? []
    ),
    loading: countriesReturnRateQuery.isLoading || loadingCountries,
    shopCurrency:
      countriesReturnRateQuery.data?.[countriesReturnRateQuery.data.length - 1]
        ?.shopCurrency ?? 'EUR'
  }
}

function toCountriesReturnRateWithCountryName(
  countries: Country[],
  countriesReturnRate: CountryReturnRate[]
) {
  return countriesReturnRate.map((entry) => {
    return {
      ...entry,
      country: toCountryName(entry.country, countries)
    }
  })
}
